import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Spin,
  App,
  Space,
  Form,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  listCompetitionRegions,
  listAllStates,
  listCitiesByState,
  listTeams,
} from '../../services/catalogs';
import { getClientsCountByFavoriteTeam } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';
import debounce from 'lodash/debounce';

const FavoriteTeamsReport = () => {
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [form] = Form.useForm();

  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const stateId = Form.useWatch('stateId', form);

  useEffect(() => {
    Promise.all([
      listCompetitionRegions(),
      listAllStates(),
      listTeams(),
    ]).then(([z, s, t]) => {
      setZones(z || []);
      setStates(s || []);
      setTeams(t || []);
    });
  }, []);

  useEffect(() => {
    if (stateId) {
      listCitiesByState(stateId).then(setCities);
    } else {
      setCities([]);
      form.setFieldValue('cityId', undefined);
    }
  }, [stateId, form]);

  const fetchCities = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || !stateId) return;
        setLoadingCities(true);
        try {
          const result = await listCitiesByState(stateId, value);
          setCities(result || []);
        } finally {
          setLoadingCities(false);
        }
      }, 500),
    [stateId]
  );

  const buildParams = () => {
    const values = form.getFieldsValue();
    const params = {};
    if (values.zoneId != null) params.zoneId = values.zoneId;
    if (values.stateId != null) params.stateId = values.stateId;
    if (values.cityId != null) params.cityId = values.cityId;
    if (values.teamId != null) params.teamId = values.teamId;
    return params;
  };

  const handleSearch = async () => {
    const params = buildParams();
    if (Object.keys(params).length < 1) {
      message.warning('Debe seleccionar al menos un filtro para consultar.');
      return;
    }
    setLoading(true);
    try {
      const res = await getClientsCountByFavoriteTeam(params);
      if (res?.ok && res?.data) {
        setData(res.data.items || []);
        setTotalCount(res.data.total ?? 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (err) {
      message.error(err?.__normalized?.message || 'Error al obtener el reporte.');
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setData([]);
    setTotalCount(0);
    setCities([]);
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning('No hay datos para exportar. Ejecute primero la búsqueda.');
      return;
    }
    setExporting(true);
    try {
      const excelData = data.map((item) => ({
        Zona: item.zona ?? '',
        Estado: item.estado ?? '',
        Ciudad: item.ciudad ?? '',
        'Equipo favorito': item.equipoFavorito ?? '',
        '#': item.cantidad ?? 0,
        '% vs Total': item.porcentajeVsTotal ?? 0,
      }));
      const columnWidths = [
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 8 },
        { wch: 12 },
      ];
      exportToExcel(
        excelData,
        columnWidths,
        'Reporte - Tribuna digital',
        'reporte_tribuna_digital'
      );
      message.success('Archivo Excel exportado correctamente.');
    } catch (err) {
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Zona',
      dataIndex: 'zona',
      key: 'zona',
      sorter: (a, b) => (a.zona || '').localeCompare(b.zona || ''),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
    },
    {
      title: 'Ciudad',
      dataIndex: 'ciudad',
      key: 'ciudad',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.ciudad || '').localeCompare(b.ciudad || ''),
    },
    {
      title: 'Equipo favorito',
      dataIndex: 'equipoFavorito',
      key: 'equipoFavorito',
      render: (v) => v ?? '-',
      sorter: (a, b) =>
        (a.equipoFavorito || '').localeCompare(b.equipoFavorito || ''),
    },
    {
      title: '#',
      dataIndex: 'cantidad',
      key: 'cantidad',
      sorter: (a, b) => (a.cantidad ?? 0) - (b.cantidad ?? 0),
      defaultSortOrder: 'descend',
    },
    {
      title: '% vs Total',
      dataIndex: 'porcentajeVsTotal',
      key: 'porcentajeVsTotal',
      render: (v) => (v != null ? `${v}%` : '-'),
      sorter: (a, b) =>
        (a.porcentajeVsTotal ?? 0) - (b.porcentajeVsTotal ?? 0),
    },
  ];

  const dataSource = data.map((item, idx) => ({ ...item, key: idx }));

  return (
    <Card
      title="Reportes - Tribuna digital"
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!data.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="zoneId" label="Zona">
              <Select
                allowClear
                placeholder="Zona"
                options={zones.map((z) => ({ value: z.id, label: z.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="stateId" label="Estado">
              <Select
                allowClear
                placeholder="Estado"
                options={states.map((s) => ({ value: s.id, label: s.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="cityId" label="Ciudad">
              <Select
                allowClear
                showSearch
                filterOption={false}
                onSearch={fetchCities}
                loading={loadingCities}
                placeholder="Ciudad"
                disabled={!stateId}
                options={cities.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="teamId" label="Equipo favorito">
              <Select
                allowClear
                placeholder="Equipo favorito"
                options={teams.map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            totalCount > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <strong>Total general</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{totalCount}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>100%</Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default FavoriteTeamsReport;
